import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';

import { setFingerprint } from '@/productDiscovery/fingerprint/fingerprint.actions';
import { useFingerprint } from '@/productDiscovery/fingerprint/hooks/useFingerprint';
import { generateFingerprint } from '@/productDiscovery/fingerprint/utils/fingerprintGenerator';
import { usePlaylistReconciliation } from '@/productDiscovery/Recommendation/modules/ProductRecommendation/hooks/usePlaylistReconciliation';

const DEBOUNCE_DELAY = 200;

function aggregateDebounce<
  Args,
  Func extends (args: Args) => void = (args: Args) => void,
  AggreatedFunc extends (args: Args[]) => void = (args: Args[]) => void,
>(aggregatedFunc: AggreatedFunc, wait: number): Func {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let storedArgs: Args[] = [];

  const debouncedFunction = ((args) => {
    storedArgs.push(args);
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = undefined;
      aggregatedFunc(storedArgs);
      storedArgs = [];
    }, wait);
  }) as Func;

  return debouncedFunction;
}

export type UseFingerprintReconciliationTrackClickItem<T> = (args: {
  fingerprint: string;
  itemIndex: number;
  item: T;
  reconciliationData: Awaited<
    ReturnType<
      ReturnType<
        typeof usePlaylistReconciliation
      >['createPlaylistReconciliation']
    >
  >;
}) => Promise<unknown> | unknown;

export type UseFingerprintReconciliationTrackDisplayItem<T> = (args: {
  fingerprint: string | null;
  itemsWithIndex: { item: T; itemIndex: number }[];
  reconciliationData: Awaited<
    ReturnType<
      ReturnType<
        typeof usePlaylistReconciliation
      >['createPlaylistReconciliation']
    >
  >;
}) => Promise<unknown> | unknown;

export type UseFingerprintReconciliationTrackDisplayed = (args: {
  fingerprint: string | null;
  reconciliationData: Awaited<
    ReturnType<
      ReturnType<
        typeof usePlaylistReconciliation
      >['createPlaylistReconciliation']
    >
  >;
}) => Promise<boolean | void> | boolean | void;

export interface UseRecommendationSliderTrackingProps<T> {
  engineTitle: string;
  eventTrigger: string;
  trackClickedItem?: UseFingerprintReconciliationTrackClickItem<T>;
  trackDisplayedItem?: UseFingerprintReconciliationTrackDisplayItem<T>;
  trackDisplayed?: UseFingerprintReconciliationTrackDisplayed;
  visibleThreshold?: number;
}

export interface ReconciliationData {
  playlistFingerprint: string;
  playlistReconciliation: string;
}

type UseFingerprintReconciliationTrackingHandler<T> = (
  itemIndex: number,
  item: T,
) => void;

interface UseFingerprintReconciliationTracking<T> {
  onClick: UseFingerprintReconciliationTrackingHandler<T>;
  onDisplay: UseFingerprintReconciliationTrackingHandler<T>;
  ref: (node?: Element | null) => void;
}

export function useFingerprintReconciliationTracking<T>({
  engineTitle,
  eventTrigger,
  trackClickedItem,
  trackDisplayedItem,
  trackDisplayed,
  visibleThreshold = 0.9,
}: UseRecommendationSliderTrackingProps<T>): UseFingerprintReconciliationTracking<T> {
  const dispatch = useDispatch();
  const displayedFingerPrint = useFingerprint();
  const eventSent = useRef(false);
  const [ref, displayed] = useInView({
    delay: 2000,
    threshold: visibleThreshold,
    triggerOnce: true,
  });

  const { createPlaylistReconciliation, persistPlaylistReconciliation } =
    usePlaylistReconciliation();

  const reconciliationData = useMemo(
    () => createPlaylistReconciliation(engineTitle, eventTrigger),
    [engineTitle, eventTrigger, createPlaylistReconciliation],
  );

  const onClick = useCallback<UseFingerprintReconciliationTrackingHandler<T>>(
    async (itemIndex, item) => {
      const fingerprint = await generateFingerprint();

      reconciliationData.then((data) => {
        Promise.resolve(
          trackClickedItem?.({
            fingerprint,
            itemIndex,
            item,
            reconciliationData: data,
          }),
        );
        dispatch(setFingerprint(fingerprint));

        persistPlaylistReconciliation(
          data.playlistFingerprint,
          data.playlistReconciliation,
          itemIndex.toString(),
        );
      });
    },
    [
      reconciliationData,
      trackClickedItem,
      dispatch,
      persistPlaylistReconciliation,
    ],
  );

  const onDisplayItems = useCallback(
    async (
      args: Parameters<UseFingerprintReconciliationTrackingHandler<T>>[],
    ) => {
      const reconciliation = await reconciliationData;
      await Promise.resolve(
        trackDisplayedItem?.({
          reconciliationData: reconciliation,
          fingerprint: displayedFingerPrint,
          itemsWithIndex: args.map(([itemIndex, item]) => ({
            item,
            itemIndex,
          })),
        }),
      );
    },
    [displayedFingerPrint, reconciliationData, trackDisplayedItem],
  );

  const onDisplayItem = useMemo<
    UseFingerprintReconciliationTrackingHandler<T>
  >(() => {
    const aggreatedFunction = aggregateDebounce<
      Parameters<UseFingerprintReconciliationTrackingHandler<T>>
    >(onDisplayItems, DEBOUNCE_DELAY);

    return (itemIndex: number, item: T) => aggreatedFunction([itemIndex, item]);
  }, [onDisplayItems]);

  useEffect(() => {
    if (!displayed || eventSent.current) {
      return undefined;
    }

    reconciliationData
      .then((data) =>
        trackDisplayed?.({
          reconciliationData: data,
          fingerprint: displayedFingerPrint,
        }),
      )
      .then((result) => {
        eventSent.current = typeof result === 'boolean' ? result : !result;
      });
  }, [displayed, displayedFingerPrint, reconciliationData, trackDisplayed]);

  return {
    onClick,
    onDisplay: onDisplayItem,
    ref,
  };
}
